<template>
	<div class="mileage">
		<div class="mileage__header">
			{{ $t('mileage.use', { currency: coinName }) }}
			<span v-if="tooltipPath" class="mileage__use__tooltip">
				<v-popover placement="right" popover-base-class="v-tooltip v-popover mileage" trigger="click">
					<template slot="popover">
						<div v-close-popover class="tooltip-close" />
					</template>
					<i class="tooltip-button" @click="handleTooltipClick"></i>
					<template slot="popover" class="tooltip-template">
						<i18n :path="`${tooltipPath}.desc`" class="text">
							<template v-slot:highlight>
								<span class="highlight">{{ $t(`${tooltipPath}.highlight`) }}</span>
							</template>
							<template v-slot:link>
								<a class="link" target="_blank" :href="tooltipLink">{{ $t(`${tooltipPath}.link`) }}</a>
							</template>
						</i18n>
					</template>
				</v-popover>
			</span>
		</div>
		<div class="mileage__separator" />
		<div class="mileage__use">
			<span v-if="!isMileagePending">
				{{ $t('mileage.my', { currency: coinName })
				}}<span class="mileage__use__banlance">{{ mileageBalance }}</span>
			</span>
			<span v-else class="mileage__use__pending-desc">
				<span>{{ $t('mileage.pending-desc') }}</span>
				<div class="mileage__use__tooltip">
					<v-popover placement="right" popover-base-class="v-tooltip v-popover" trigger="click">
						<template slot="popover">
							<div v-close-popover class="tooltip-close" />
						</template>
						<i class="tooltip-button"></i>
						<template slot="popover" class="tooltip-template">
							<i18n path="mileage.pending-tooltip" class="text">
								<template v-slot:supportLink>
									<a
										class="link"
										target="_blank"
										href="https://playersupport.nexon.com/hc/en-us?_ga=2.65007915.583200399.1741932959-15862622.1741932959"
										>{{ $t('checkout.nexon-support-center') }}</a
									>
								</template>
							</i18n>
						</template>
					</v-popover>
				</div>
			</span>
			<div class="mileage__use__form">
				<FormInput
					id="InputMileage"
					v-model="inputMileage"
					class="mileage-input"
					:placeholder="placeholder"
					:has-error="error.code !== ''"
					:override-error-text="error.message"
					:disabled="isMileagePending"
					@keydown="keydownAction"
					@blur="onBlur"
				/>
				<FormButton
					class="btn-primary"
					:button-click="useAllAction"
					:loading="useAllLoading"
					:label="$t('mileage.use-all')"
					:disabled="isMileagePending"
				/>
			</div>
		</div>
		<div class="mileage__header">
			{{ $t('mileage.purchase-summary') }}
		</div>
		<div class="mileage__separator" />
		<div class="mileage__summary">
			<div class="mileage__summary__container" :class="{ pending: isMileagePending }">
				<div class="mileage__summary__container__item">
					<span>{{ $t('mileage.order-total') }}</span>
					<span><currency :code="paymentInfo.currency" :value="Number(orderTotal)"/></span>
				</div>
				<div class="mileage__summary__container__item">
					<span>{{ $t('mileage.use', { currency: coinName }) }}</span>
					<span>{{ mileageAmount }} {{ unitName }}</span>
				</div>
			</div>
			<div class="mileage__summary__final" :class="{ pending: isMileagePending }">
				<span>
					{{ $t('mileage.final-amount') }}
				</span>
				<span class="mileage__summary__final__value"
					><currency :code="paymentInfo.currency" :value="Number(paidAmount)"
				/></span>
			</div>
		</div>
		<div class="mileage__btn__container">
			<FormButton
				:button-click="continueAction"
				:loading="continueLoading"
				:label="$t('checkout.continue-purchase')"
				:disabled="error.code !== '' || checkMileage"
			/>
			<FormButton
				class="btn-primary"
				:label="$t('cancel')"
				:button-click="cancelAction"
				:loading="cancelLoading"
			/>
		</div>
		<License
			:style="{
				marginTop: '16px',
			}"
			@termClick="handleTermClick"
			@supportClick="handleSupportClick"
		/>
	</div>
</template>

<script>
import { FormButton, FormInput } from '@arena/toolkit';
import currency from '@/components/common/currency';
import License from '@/components/common/license';

const TOOLTIP_MAP = {
	S59777: {
		path: 'mileage.tooltip.hit2',
		link: 'https://tw.nexon.com/hit2/zh/events/h2c',
	}, // HIT2 live
	S59767: {
		path: 'mileage.tooltip.hit2',
		link: 'https://tw.nexon.com/hit2/zh/events/h2c',
	}, // HIT2 test
	S41226: {
		path: 'mileage.tooltip.hit2',
		link: 'https://tw.nexon.com/hit2/zh/events/h2c',
	}, // for test
};

export default {
	name: 'InGameMileage',
	components: {
		FormButton,
		FormInput,
		currency,
		License,
	},
	props: {
		cancelLoading: {
			type: Boolean,
			default: false,
		},
		continueLoading: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			useAllLoading: false,
			inputMileage: null,
			checkMileageUseHandler: -1,
			paidAmount: 0,
			mileageAmount: 0,
			error: {
				code: '',
				message: '',
			},
			checkMileage: false,
			TOOLTIP_MAP: TOOLTIP_MAP,
		};
	},
	validations: {},
	computed: {
		mileageBalance() {
			return this.$store.state.mileage.mileageBalance;
		},
		coinName() {
			return this.$store.state.mileage.coinName;
		},
		unitName() {
			return this.$store.state.mileage.unitName;
		},
		isMileagePending() {
			return this.$store.state.mileage.hasFailMileagePayment;
		},
		paymentInfo() {
			return this.inGameOrderData.payment;
		},
		inGameOrderData() {
			return this.$store.state.inGameOrderData;
		},
		orderTotal() {
			return this.inGameOrderData.order.total;
		},
		placeholder() {
			return this.isMileagePending
				? this.$t('mileage.input.placeholder-can-not-use', { currency: this.coinName })
				: this.$t('mileage.input.placeholder', { currency: this.coinName });
		},
		canUseDecimalPoint() {
			return this.region !== 'tw' && this.region !== 'th';
		},
		curServiceCode() {
			return this.inGameOrderData && this.inGameOrderData.order && this.inGameOrderData.order.serviceCode;
		},
		tooltipPath() {
			return this.TOOLTIP_MAP[this.curServiceCode] && this.TOOLTIP_MAP[this.curServiceCode].path;
		},
		tooltipLink() {
			return this.TOOLTIP_MAP[this.curServiceCode] && this.TOOLTIP_MAP[this.curServiceCode].link;
		},
		isIngamePurchase() {
			return this.$route.name === 'in-game-purchase' || this.$store.state.appType === 'game';
		},
	},
	watch: {
		inputMileage() {
			if (this.checkMileageUseHandler !== -1) {
				clearTimeout(this.checkMileageUseHandler);
				this.checkMileageUseHandler = -1;
			}

			this.checkMileage = true;
			this.checkMileageUseHandler = setTimeout(() => {
				this.checkMileageUse();
			}, 200);
		},
	},
	mounted() {
		this.paidAmount = this.orderTotal - this.inputMileage;

		const inputEl = document.querySelector('#InputMileage');
		if (inputEl && inputEl.addEventListener) {
			inputEl.addEventListener('paste', this.handlePastePrevent, true);
		}
	},
	methods: {
		handleTermClick() {
			this.$a2s.click('location_terms');
		},
		handleSupportClick() {
			this.$a2s.click('location_support');
		},
		handleTooltipClick() {
			this.$a2s.click('mileage_info');
		},
		handlePastePrevent(e) {
			e.preventDefault();
		},
		cancelAction() {
			this.$emit('cancel');
		},
		continueAction() {
			this.$emit('continue', this.mileageAmount, this.paidAmount);
		},
		useAllAction() {
			this.$a2s.click('mileage_use_all');
			this.useAllLoading = true;
			if (this.mileageBalance > 0) {
				this.inputMileage =
					this.mileageBalance > this.orderTotal ? String(this.orderTotal) : String(this.mileageBalance);
			}
			setTimeout(() => {
				this.useAllLoading = false;
			}, 0);
		},
		isValidDecimalPoint() {
			if (this.canUseDecimalPoint) {
				if (this.inputMileage && this.inputMileage.includes('.')) {
					const decimalPart = this.inputMileage.split('.')[1];
					return decimalPart.length <= 1;
				}
			}
			return true;
		},
		keydownAction(e) {
			if (
				e.key !== 'Backspace' &&
				e.key !== '.' &&
				e.key !== 'Delete' &&
				e.key !== 'ArrowLeft' &&
				e.key !== 'ArrowRight' &&
				e.key !== 'ArrowUp' &&
				e.key !== 'ArrowDown' &&
				e.ctrlKey !== true &&
				e.shiftKey !== true &&
				e.altKey !== true &&
				Number.isNaN(parseInt(e.key))
			) {
				e.preventDefault();
				return;
			}
			if (!this.canUseDecimalPoint && e.key === '.') {
				e.preventDefault();
				return;
			}
			if (this.canUseDecimalPoint && e.key === '.' && this.inputMileage && this.inputMileage.includes('.')) {
				e.preventDefault();
				return;
			}
			if (!this.isValidDecimalPoint() && e.key !== 'Backspace') {
				e.preventDefault();
				return;
			}
		},
		isNumber(value) {
			return (
				value === '' ||
				!Number.isNaN(parseInt(value)) ||
				(value !== '.' && (value.startsWith('.') || value.endsWith('.')))
			);
		},
		onBlur() {
			if (this.error.code !== '') {
				this.inputMileage = '';
				return;
			}

			if (this.inputMileage) {
				if (this.inputMileage.length > 1 && this.inputMileage.startsWith('0')) {
					this.inputMileage = this.inputMileage.replace(/^0+/, '');
				}

				if (this.inputMileage.startsWith('.') && this.canUseDecimalPoint) {
					this.inputMileage = '0' + this.inputMileage;
				}

				if (this.inputMileage.endsWith('.') && this.canUseDecimalPoint) {
					this.inputMileage = this.inputMileage + '00';
				}
			}
		},
		async checkMileageUse() {
			try {
				if (!this.isNumber(this.inputMileage)) {
					this.error.code = 'invalid';
					this.error.message = this.canUseDecimalPoint
						? this.$t('mileage.input.error.not-number')
						: this.$t('mileage.input.error.not-decimal-point');
					return;
				}

				if (this.region === 'tw' || this.region === 'th') {
					if (!Number.isInteger(Number(this.inputMileage))) {
						this.error.code = 'invalid';
						this.error.message = this.$t('mileage.input.error.integer-only', {
							currency: this.coinName,
						});
						return;
					}
				}

				if (this.isNumber(this.inputMileage) && parseFloat(this.inputMileage) < 0) {
					this.error.code = 'invalid';
					this.error.message = this.$t('mileage.input.error.other');
					return;
				}

				if (this.canUseDecimalPoint) {
					if (this.inputMileage.includes('.') && this.inputMileage.split('.')[1].length > 2) {
						this.error.code = 'invalid';
						this.error.message = this.$t('mileage.input.error.decimal-point');
						return;
					}
				}

				const response = await this.$checkoutAPI.checkMileageUsage(this.inputMileage || 0);
				this.paidAmount = String(response.data.paidAmount);
				this.mileageAmount = String(response.data.mileageAmount);
				this.error.code = '';
				this.error.message = '';
			} catch (error) {
				this.error.code = error.response.data.code || 'unknown';
				if (this.error.code === '002-400-0017') {
					this.error.message = this.$t('mileage.input.error.more-than-balance', { currency: this.coinName });
				} else if (this.error.code === '002-400-0018') {
					const amount = error.response.data.message.split('|')[1];
					this.error.message = this.$t('mileage.input.error.minimum-usage', {
						amount: `${this.unitName || ''}${amount === undefined ? '0.01' : ''}`,
					});
				} else if (this.error.code === '002-400-0019') {
					if (this.region === 'global' || this.region === 'sea') {
						this.error.message = this.$t('mileage.input.error.west-minimum', {
							currency: this.coinName,
							amount: this.formattedCurrency(this.paymentInfo.currency, 1),
						});
					} else if (this.region === 'tw') {
						this.error.message = this.$t('mileage.input.error.tw-minimum', { currency: this.coinName });
					} else {
						this.error.message = this.$t('mileage.input.error.other');
					}
				} else {
					this.error.message = this.$t('mileage.input.error.other');
				}
			} finally {
				this.checkMileage = false;
			}
		},
		formattedCurrency(code, value) {
			if (code === 'NX') {
				return `${this.$n(value)} ${code}`;
			} else if (code === 'TWD') {
				return `${this.$n(value)} ${code}`;
			} else if (this.$n(value, 'currency', code)) {
				return this.$n(value, 'currency', code).split('.')[0]; // 소수점 이하 제거
			}
			return `${this.$n(value)} ${code}`;
		},
	},
};
</script>

<style lang="scss" scoped>
.mileage {
	padding: 16px 20px 20px 20px;
	border-radius: 2px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	background: #2a2a2a;
	font-size: 14px;
	font-weight: 400;
	color: rgba(255, 255, 255, 0.5);

	&__header {
		color: white;
		font-size: 18px;
		font-weight: 700;
		margin-bottom: 16px;
		display: flex;
		align-items: center;
		gap: 6px;
	}

	&__separator {
		height: 2px;
		opacity: 0.5;
		background-color: white;
	}

	&__use {
		margin-top: 24px;

		&__banlance {
			margin-left: 8px;
			color: white;
			font-weight: 700;
		}

		&__pending-desc {
			font-size: 14px;
			font-weight: 400;
			color: #e63f57;
			line-height: 20px;
		}

		&__tooltip {
			position: relative;
			display: inline-block;
			top: -1px;
			margin-left: 5px;
			font-size: 13px;
			color: #666666;
			> .tooltip-button {
				margin-left: 5px;
			}

			.v-popover {
				.tooltip-button {
					display: inline-flex;
					justify-content: center;
					align-items: center;
					border-radius: 50%;
					font-family: Arial;
					font-style: normal;
					font-weight: 700;
					font-size: 8.5px;
					width: 14px;
					height: 14px;
					background-color: #4f5159;
					color: hsla(0, 0%, 100%, 0.8);
					&:before {
						content: '?';
						line-height: 1;
					}
					&:hover {
						background-color: #7b7f8c;
						cursor: pointer;
					}
				}

				&.open {
					.tooltip-button {
						background-color: #7b7f8c;
					}
				}
			}
		}

		&__form {
			display: flex;
			align-items: flex-start;
			margin-top: 16px;
			margin-bottom: 16px;

			&::v-deep .form-group {
				.form-control {
					&:disabled {
						background-color: rgba(12, 12, 12, 0.2);
						border: none;

						&::placeholder {
							color: rgba(255, 255, 255, 0.2);
						}
					}
				}
			}

			.btn-primary {
				&:disabled {
					background-color: #333;
					border: none;
					color: rgba(255, 255, 255, 0.1);
				}
			}

			.mileage-input {
				margin-bottom: 0;
				flex-grow: 1;
			}

			.btn-primary {
				margin-top: 0;
				width: 98px;
				margin-left: 10px;
				text-transform: unset;
			}
		}
	}

	&__summary {
		margin-top: 13px;
		margin-bottom: 20px;

		&__container {
			padding: 16px;
			border-radius: 1px;
			background-color: rgba(255, 255, 255, 0.05);
			display: flex;
			flex-direction: column;
			gap: 16px;

			&__item {
				display: flex;
				justify-content: space-between;
			}

			&.pending {
				background-color: unset;
				padding: 0px;
			}
		}

		&__final {
			margin-top: 10px;
			height: 28px;
			text-align: right;
			display: flex;
			justify-content: flex-end;
			align-items: center;
			gap: 19px;

			&__value {
				font-size: 24px;
				font-weight: 700;
				color: white;
			}

			&.pending {
				margin-top: 24px;
				display: flex;
				justify-content: space-between;
				color: white;
				font-size: 14px;
				font-weight: 700;
			}
		}

		&__separator {
			height: 1px;
			opacity: 0.1;
			background-color: white;
		}
	}

	&__btn__container {
		display: flex;
		flex-direction: column;
		gap: 10px;

		.btn {
			margin-top: 0;
		}
	}
}
</style>
<style lang="scss">
.v-tooltip {
	.wrapper {
		top: 13px;
		right: calc(100% + 5px);
		color: red;
		position: absolute;
		width: 254px;

		.tooltip-inner {
			padding: 20px;
			.text {
				white-space: pre-wrap;
				font-size: 11px;

				.highlight {
					color: #e63f57;
				}
				.link {
					color: #0077ff;
					text-decoration: underline;
					text-transform: uppercase;
				}
			}
		}

		@media (max-width: 513px) {
			position: absolute;
			top: 12px;
			left: 100%;
		}

		@media (max-width: 418px) {
			position: absolute;
			top: 12px;
			left: -80px;
		}

		@media (max-width: 374px) {
			position: absolute;
			top: 12px;
			left: -150px;
		}

		@media (max-width: 347px) {
			position: absolute;
			top: 12px;
			left: -200px;
		}
	}

	&.mileage {
		.wrapper {
			position: absolute;
			top: -9px;
			left: 3px;

			@media (max-width: 440px) {
				top: 20px;
				left: -122px;
			}
		}
	}
}
</style>
